import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';

import Icon from 'components/AppIcon';
import PrimaryNavigation from 'components/ui/PrimaryNavigation';


import ScannerViewfinder from './components/ScannerViewfinder';
import ProductInfoPanel from './components/ProductInfoPanel';
import AlternativesPanel from './components/AlternativesPanel';
import ManualSearchPanel from './components/ManualSearchPanel';
import ScannerControls from './components/ScannerControls';
import BudgetStatusBar from './components/BudgetStatusBar';
import PurchaseDecisionModal from './components/PurchaseDecisionModal';
import ARScannerErrorCard from './components/ARScannerErrorCard';
import PurchaseSuccessToast from './components/PurchaseSuccessToast';
import { useAuth } from '../../contexts/AuthContext';
import { fetchBudgetForScanner, logARPurchase, subscribeToBudgetUpdates } from '../../services/arScannerService';

// ─── Mock Products (kept for AR simulation) ───────────────────────────────────
const MOCK_PRODUCTS = [
{
  id: 1,
  name: "Sony WH-1000XM5 Wireless Noise Canceling Headphones",
  brand: "Sony",
  price: 279.99,
  originalPrice: 349.99,
  category: "Shopping",
  image: "https://img.rocket.new/generatedImages/rocket_gen_img_1c4b2e322-1768327279480.png",
  imageAlt: "Sony WH-1000XM5 premium wireless headphones in black color displayed on white surface with clean minimalist background",
  rating: 4.8,
  priceComparison: [
  { store: "Amazon", price: 279.99 },
  { store: "Best Buy", price: 299.99 },
  { store: "Walmart", price: 289.99 },
  { store: "Target", price: 309.99 }]

},
{
  id: 2,
  name: "Apple AirPods Pro (2nd Generation)",
  brand: "Apple",
  price: 199.99,
  originalPrice: 249.99,
  category: "Shopping",
  image: "https://images.unsplash.com/photo-1631677624302-55e6178078f1",
  imageAlt: "Apple AirPods Pro second generation white earbuds with charging case on light gray background",
  rating: 4.7,
  priceComparison: [
  { store: "Apple Store", price: 249.99 },
  { store: "Amazon", price: 199.99 },
  { store: "Best Buy", price: 219.99 },
  { store: "Costco", price: 189.99 }]

},
{
  id: 3,
  name: "Samsung 55\" 4K QLED Smart TV",
  brand: "Samsung",
  price: 649.99,
  originalPrice: 799.99,
  category: "Shopping",
  image: "https://img.rocket.new/generatedImages/rocket_gen_img_1bb34276d-1771891212623.png",
  imageAlt: "Samsung 55 inch 4K QLED smart television mounted on wall displaying vibrant colorful nature scene",
  rating: 4.6,
  priceComparison: [
  { store: "Samsung.com", price: 799.99 },
  { store: "Best Buy", price: 649.99 },
  { store: "Amazon", price: 679.99 },
  { store: "Costco", price: 629.99 }]

},
{
  id: 4,
  name: "Nike Air Max 270 Running Shoes",
  brand: "Nike",
  price: 129.99,
  originalPrice: 150.00,
  category: "Shopping",
  image: "https://img.rocket.new/generatedImages/rocket_gen_img_131b5c4f0-1772793887193.png",
  imageAlt: "Nike Air Max 270 white and black running shoes displayed on clean white background with visible air cushion sole",
  rating: 4.5,
  priceComparison: [
  { store: "Nike.com", price: 150.00 },
  { store: "Amazon", price: 129.99 },
  { store: "Foot Locker", price: 139.99 },
  { store: "DSW", price: 134.99 }]

},
{
  id: 5,
  name: "Instant Pot Duo 7-in-1 Electric Pressure Cooker",
  brand: "Instant Pot",
  price: 79.99,
  originalPrice: 99.99,
  category: "Groceries",
  image: "https://img.rocket.new/generatedImages/rocket_gen_img_19efd232d-1772074801431.png",
  imageAlt: "Instant Pot Duo 7-in-1 stainless steel electric pressure cooker on kitchen counter with control panel visible",
  rating: 4.9,
  priceComparison: [
  { store: "Amazon", price: 79.99 },
  { store: "Target", price: 89.99 },
  { store: "Walmart", price: 84.99 },
  { store: "Costco", price: 74.99 }]

}];


const MOCK_ALTERNATIVES = [
{
  id: 101,
  name: "Anker Soundcore Q45 Headphones",
  brand: "Anker",
  price: 59.99,
  image: "https://img.rocket.new/generatedImages/rocket_gen_img_15c451327-1772823613498.png",
  imageAlt: "Anker Soundcore Q45 over-ear wireless headphones in black color on neutral background showing cushioned ear cups"
},
{
  id: 102,
  name: "JBL Tune 760NC Wireless",
  brand: "JBL",
  price: 99.95,
  image: "https://img.rocket.new/generatedImages/rocket_gen_img_1dc0858fc-1765380068931.png",
  imageAlt: "JBL Tune 760NC wireless over-ear headphones in blue color with foldable design on white background"
},
{
  id: 103,
  name: "Jabra Evolve2 55 Headset",
  brand: "Jabra",
  price: 149.99,
  image: "https://img.rocket.new/generatedImages/rocket_gen_img_1e2acf10e-1772783699395.png",
  imageAlt: "Jabra Evolve2 55 professional wireless headset in black with microphone boom arm on desk surface"
}];


const SCAN_SEQUENCE_DELAY = 2200;

const DEFAULT_BUDGET = {
  totalBudget: 0,
  totalSpent: 0,
  category: 'Shopping',
  categoryBudget: 400,
  categorySpent: 0
};

// ─── Main Component ───────────────────────────────────────────────────────────
export default function ARScannerInterface() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const scanTimerRef = useRef(null);
  const metadataTimerRef = useRef(null);

  const [cameraActive, setCameraActive] = useState(false);
  const [cameraError, setCameraError] = useState(null);
  const [errorState, setErrorState] = useState({ type: null, retryCount: 0 });
  const [isRetrying, setIsRetrying] = useState(false);
  const [isScanning, setIsScanning] = useState(false);
  const [isDetected, setIsDetected] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [detectedProduct, setDetectedProduct] = useState(null);
  const [flashOn, setFlashOn] = useState(false);
  const [showManualSearch, setShowManualSearch] = useState(false);
  const [showPurchaseModal, setShowPurchaseModal] = useState(false);
  const [savedToWishlist, setSavedToWishlist] = useState(false);
  const [toastMsg, setToastMsg] = useState(null);
  const [purchaseToast, setPurchaseToast] = useState(null);
  const [scanCount, setScanCount] = useState(0);

  // Real-time budget state
  const [budgetData, setBudgetData] = useState(DEFAULT_BUDGET);
  const [budgetLoading, setBudgetLoading] = useState(true);

  // Load initial budget data
  useEffect(() => {
    if (!user?.id) return;
    setBudgetLoading(true);
    fetchBudgetForScanner(user?.id)?.then((data) => setBudgetData(data))?.catch((err) => console.error('Budget fetch error:', err))?.finally(() => setBudgetLoading(false));
  }, [user?.id]);

  // Subscribe to real-time budget updates
  useEffect(() => {
    if (!user?.id) return;
    const unsubscribe = subscribeToBudgetUpdates(user?.id, (updatedData) => {
      setBudgetData(updatedData);
    });
    return unsubscribe;
  }, [user?.id]);

  const showToast = useCallback((msg) => {
    setToastMsg(msg);
    setTimeout(() => setToastMsg(null), 3000);
  }, []);

  const startCamera = useCallback(async () => {
    try {
      setCameraError(null);

      // ── 1. Browser support check ──────────────────────────────────────────
      if (!navigator.mediaDevices?.getUserMedia) {
        console.error('[ARScanner] Unsupported browser: navigator.mediaDevices.getUserMedia is not available');
        setErrorState((prev) => ({ type: 'unsupported', retryCount: prev?.retryCount }));
        setIsRetrying(false);
        return;
      }

      // ── 2. Wrap getUserMedia in a 10-second timeout ───────────────────────
      const getUserMediaWithTimeout = () => {
        const mediaPromise = navigator.mediaDevices?.getUserMedia({
          video: { facingMode: 'environment', width: { ideal: 1280 }, height: { ideal: 720 } },
        });
        const timeoutPromise = new Promise((_, reject) =>
          setTimeout(() => reject(new Error('StreamTimeout')), 10000)
        );
        return Promise.race([mediaPromise, timeoutPromise]);
      };

      const stream = await getUserMediaWithTimeout();
      streamRef.current = stream;

      if (videoRef?.current) {
        videoRef.current.srcObject = stream;

        // ── 3. 10-second metadata timeout after srcObject assignment ─────────
        if (metadataTimerRef?.current) clearTimeout(metadataTimerRef?.current);
        metadataTimerRef.current = setTimeout(() => {
          if (videoRef?.current && videoRef?.current?.readyState < 2) {
            console.error('[ARScanner] Stream timeout: video.onLoadedMetadata did not fire within 10 seconds');
            setErrorState((prev) => ({ type: 'timeout', retryCount: prev?.retryCount }));
            setIsRetrying(false);
          }
        }, 10000);
      } else {
        setCameraActive(false);
      }
    } catch (err) {
      const name = err?.name || err?.message || '';

      if (name === 'StreamTimeout') {
        console.error('[ARScanner] Stream timeout: getUserMedia did not resolve within 10 seconds');
        setErrorState((prev) => ({ type: 'timeout', retryCount: prev?.retryCount }));
      } else if (
        name === 'NotFoundError' ||
        name === 'DevicesNotFoundError' ||
        name === 'OverconstrainedError'
      ) {
        console.error('[ARScanner] Camera device not found:', name);
        setErrorState((prev) => ({ type: 'not_found', retryCount: prev?.retryCount }));
      } else if (name === 'NotAllowedError' || name === 'PermissionDeniedError') {
        console.error('[ARScanner] Camera permission denied:', name);
        setCameraError('Camera permission denied. Please allow camera access to use AR scanning.');
      } else {
        console.error('[ARScanner] Unable to access camera:', name);
        setCameraError('Unable to access camera. Please check your device settings.');
      }
      setIsRetrying(false);
    }
  }, []);

  const stopCamera = useCallback(() => {
    if (metadataTimerRef?.current) clearTimeout(metadataTimerRef?.current);
    if (streamRef?.current) {
      streamRef?.current?.getTracks()?.forEach((t) => t?.stop());
      streamRef.current = null;
    }
    setCameraActive(false);
  }, []);

  useEffect(() => {
    startCamera();
    return () => {
      stopCamera();
      if (scanTimerRef?.current) clearTimeout(scanTimerRef?.current);
    };
  }, [startCamera, stopCamera]);

  const handleCapture = useCallback(() => {
    if (isScanning || isDetected) return;
    setIsScanning(true);
    setIsProcessing(true);
    setSavedToWishlist(false);
    scanTimerRef.current = setTimeout(() => {
      const product = MOCK_PRODUCTS?.[scanCount % MOCK_PRODUCTS?.length];
      setDetectedProduct(product);
      setIsDetected(true);
      setIsScanning(false);
      setIsProcessing(false);
      setScanCount((c) => c + 1);
    }, SCAN_SEQUENCE_DELAY);
  }, [isScanning, isDetected, scanCount]);

  const handleReset = useCallback(() => {
    setIsDetected(false);
    setDetectedProduct(null);
    setIsScanning(false);
    setIsProcessing(false);
    setSavedToWishlist(false);
    if (scanTimerRef?.current) clearTimeout(scanTimerRef?.current);
  }, []);

  const handleSaveWishlist = useCallback(() => {
    setSavedToWishlist(true);
    showToast(`${detectedProduct?.name} saved to wishlist!`);
  }, [detectedProduct, showToast]);

  const handleProceed = useCallback(() => {
    setShowPurchaseModal(true);
  }, []);

  const handleConfirmPurchase = useCallback(async () => {
    setShowPurchaseModal(false);
    const productPrice = detectedProduct?.price || 0;
    const categoryBudget = budgetData?.categoryBudget || 0;
    const categorySpent = budgetData?.categorySpent || 0;
    const remainingBudget = categoryBudget - categorySpent - productPrice;

    if (user?.id && detectedProduct) {
      try {
        await logARPurchase(user?.id, detectedProduct, budgetData?.categoryId || null);
      } catch (err) {
        // log silently
      }
    }

    setPurchaseToast({
      productName: detectedProduct?.name,
      price: productPrice,
      category: detectedProduct?.category || budgetData?.category || 'Shopping',
      remainingBudget,
      categoryBudget,
    });

    setTimeout(() => handleReset(), 1500);
  }, [handleReset, user, detectedProduct, budgetData]);

  const handleAlternativeSelect = useCallback((alt) => {
    setDetectedProduct((prev) => ({ ...prev, ...alt, priceComparison: prev?.priceComparison || [] }));
    showToast(`Switched to ${alt?.name}`);
  }, [showToast]);

  const handleRetryCamera = useCallback(() => {
    setErrorState((prev) => {
      const nextCount = prev?.retryCount + 1;
      return { type: prev?.type, retryCount: nextCount };
    });
    setIsRetrying(true);
    stopCamera();
    setCameraError(null);
    // Small delay to allow stream cleanup before retrying
    setTimeout(() => {
      startCamera()?.finally(() => setIsRetrying(false));
    }, 400);
  }, [startCamera, stopCamera]);

  const handleDismissError = useCallback(() => {
    setErrorState({ type: null, retryCount: 0 });
    setCameraError(null);
  }, []);

  return (
    <div className="min-h-screen bg-[#020617] flex flex-col">
      <PrimaryNavigation />
      {/* Main scanner area */}
      <div className="flex-1 flex flex-col lg:flex-row pt-0 md:pt-0 pb-16 md:pb-0">

        {/* Camera / Scanner Panel */}
        <div className="relative flex-1 lg:flex-[2] min-h-[50vh] lg:min-h-0 bg-slate-950 overflow-hidden">
          {/* Video feed - always rendered so ref is available before stream starts */}
          <video
            ref={videoRef}
            className={`absolute inset-0 w-full h-full object-cover ${cameraActive ? 'block' : 'hidden'}`}
            autoPlay
            playsInline
            muted
            onLoadedMetadata={() => {
              if (metadataTimerRef?.current) clearTimeout(metadataTimerRef?.current);
              setErrorState({ type: null, retryCount: 0 });
              setIsRetrying(false);
              setCameraActive(true);
              videoRef?.current?.play()?.catch(() => {});
            }}
          />

          {/* Dark overlay when no camera */}
          {!cameraActive && !cameraError && !errorState?.type &&
          <div className="absolute inset-0 bg-gradient-to-br from-slate-900 to-slate-950 flex items-center justify-center">
              <div className="text-center">
                <div className="w-16 h-16 rounded-full bg-cyan-500/10 border border-cyan-500/30 flex items-center justify-center mx-auto mb-4 animate-pulse">
                  <Icon name="Camera" size={32} color="#06B6D4" />
                </div>
                <p className="text-slate-400 text-sm">Initializing camera...</p>
              </div>
            </div>
          }

          {/* Structured error cards (unsupported / not_found / timeout) */}
          <AnimatePresence>
            {errorState?.type && (
              <ARScannerErrorCard
                errorState={errorState}
                onRetry={handleRetryCamera}
                onManualEntry={() => { handleDismissError(); setShowManualSearch(true); }}
                isRetrying={isRetrying}
              />
            )}
          </AnimatePresence>

          {/* Legacy camera error (permission denied / generic) */}
          {cameraError && !errorState?.type &&
          <div className="absolute inset-0 bg-slate-950 flex items-center justify-center p-6">
              <div className="text-center max-w-sm">
                <div className="w-16 h-16 rounded-full bg-red-500/10 border border-red-500/30 flex items-center justify-center mx-auto mb-4">
                  <Icon name="CameraOff" size={32} color="#EF4444" />
                </div>
                <h3 className="font-heading font-semibold text-slate-200 mb-2">Camera Unavailable</h3>
                <p className="text-slate-400 text-sm mb-5">{cameraError}</p>
                <div className="flex flex-col gap-3">
                  <button
                  onClick={startCamera}
                  className="px-6 py-3 bg-cyan-500 text-slate-900 font-semibold rounded-xl hover:bg-cyan-400 transition-colors min-h-[44px]">
                  
                    Try Again
                  </button>
                  <button
                  onClick={() => setShowManualSearch(true)}
                  className="px-6 py-3 bg-slate-800 text-slate-300 font-medium rounded-xl border border-slate-700 hover:border-cyan-500/40 transition-colors min-h-[44px]">
                  
                    Search Manually
                  </button>
                </div>
              </div>
            </div>
          }

          {/* Gradient overlays */}
          {cameraActive &&
          <>
              <div className="absolute inset-0 bg-gradient-to-b from-slate-950/60 via-transparent to-slate-950/80 pointer-events-none" />
            </>
          }

          {/* Real-time budget status bar */}
          <BudgetStatusBar budgetData={budgetData} />

          {/* Viewfinder */}
          {cameraActive && !isDetected &&
          <ScannerViewfinder
            isScanning={isScanning}
            isDetected={isDetected}
            isProcessing={isProcessing}
            productName={detectedProduct?.name} />

          }

          {/* Detected overlay */}
          {isDetected &&
          <div className="absolute inset-0 pointer-events-none">
              <div className="absolute inset-0 border-2 border-cyan-400/30 rounded-none" />
              <div className="absolute top-16 left-4 right-4 flex items-center gap-2 bg-slate-900/80 backdrop-blur-sm rounded-xl px-4 py-2.5 border border-cyan-500/30">
                <div className="w-2 h-2 rounded-full bg-cyan-400 animate-pulse" />
                <span className="text-cyan-400 text-sm font-medium font-heading">Product Identified</span>
                <span className="ml-auto text-slate-400 text-xs font-data">{detectedProduct?.brand}</span>
              </div>
            </div>
          }

          {/* Controls */}
          {cameraActive &&
          <ScannerControls
            isScanning={isScanning}
            isDetected={isDetected}
            onCapture={handleCapture}
            onToggleFlash={() => setFlashOn((f) => !f)}
            onSearch={() => setShowManualSearch(true)}
            flashOn={flashOn}
            onReset={handleReset} />

          }

          {/* Scan hint */}
          {cameraActive && !isScanning && !isDetected &&
          <div className="absolute bottom-28 left-0 right-0 flex justify-center pointer-events-none">
              <span className="text-slate-400 text-xs bg-slate-900/70 px-4 py-1.5 rounded-full border border-slate-700 font-caption">
                Point camera at a product and tap capture
              </span>
            </div>
          }
        </div>

        {/* Info Panel - Desktop sidebar / Mobile slide-up */}
        <div className="lg:flex-1 lg:max-w-md lg:overflow-y-auto lg:border-l lg:border-slate-800 relative">
          {/* Desktop info panel */}
          <div className="hidden lg:block h-full">
            {!isDetected ?
            <div className="h-full flex flex-col items-center justify-center p-8 text-center">
                <div className="w-20 h-20 rounded-2xl bg-cyan-500/10 border border-cyan-500/20 flex items-center justify-center mb-5">
                  <Icon name="ScanLine" size={36} color="#06B6D4" />
                </div>
                <h3 className="font-heading font-bold text-xl text-slate-200 mb-2">AR Product Scanner</h3>
                <p className="text-slate-400 text-sm leading-relaxed mb-6">
                  Point your camera at any product to instantly see price comparisons, budget impact, and smarter alternatives.
                </p>
                <div className="w-full space-y-3 text-left">
                  {[
                { icon: 'DollarSign', label: 'Real-time price comparison', desc: 'Compare prices across major retailers' },
                { icon: 'PieChart', label: 'Budget impact analysis', desc: 'See how purchases affect your goals' },
                { icon: 'Sparkles', label: 'Smart alternatives', desc: 'Find budget-friendly substitutes' }]?.map((f, i) =>
                <div key={i} className="flex items-start gap-3 bg-slate-800/40 rounded-xl p-3 border border-slate-700/50">
                      <div className="w-9 h-9 rounded-lg bg-cyan-500/10 flex items-center justify-center flex-shrink-0">
                        <Icon name={f?.icon} size={18} color="#06B6D4" />
                      </div>
                      <div>
                        <p className="text-sm font-medium text-slate-200">{f?.label}</p>
                        <p className="text-xs text-slate-500 mt-0.5">{f?.desc}</p>
                      </div>
                    </div>
                )}
                </div>
                <button
                onClick={() => setShowManualSearch(true)}
                className="mt-6 w-full py-3 rounded-xl border border-slate-700 text-slate-300 text-sm font-medium hover:border-cyan-500/40 hover:text-cyan-400 transition-colors flex items-center justify-center gap-2 min-h-[44px]">
                
                  <Icon name="Search" size={16} color="currentColor" />
                  Search product manually
                </button>
              </div> :

            <div className="p-4 space-y-0">
                {/* Product header */}
                <div className="flex items-start gap-4 mb-4 pt-2">
                  <div className="w-20 h-20 rounded-xl overflow-hidden flex-shrink-0 bg-slate-800">
                    <img
                    src={detectedProduct?.image}
                    alt={detectedProduct?.imageAlt}
                    className="w-full h-full object-cover"
                    onError={(e) => {e.target.src = '/assets/images/no_image.png';}} />
                  
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="font-heading font-semibold text-base text-slate-50 line-clamp-2">{detectedProduct?.name}</h3>
                    <p className="text-slate-400 text-sm mt-0.5">{detectedProduct?.brand}</p>
                    <div className="flex items-center gap-2 mt-1">
                      <span className="font-data text-xl font-bold text-cyan-400">${detectedProduct?.price?.toFixed(2)}</span>
                      {detectedProduct?.originalPrice &&
                    <span className="font-data text-sm text-slate-500 line-through">${detectedProduct?.originalPrice?.toFixed(2)}</span>
                    }
                    </div>
                  </div>
                </div>

                {/* Budget impact using real data */}
                {(() => {
                const budgetRemaining = budgetData?.categoryBudget - budgetData?.categorySpent;
                const afterPurchase = budgetRemaining - (detectedProduct?.price || 0);
                const canAfford = afterPurchase >= 0;
                const budgetPercent = budgetData?.categoryBudget > 0 ? Math.min(100, Math.round(budgetData?.categorySpent / budgetData?.categoryBudget * 100)) : 0;
                const afterPercent = budgetData?.categoryBudget > 0 ? Math.min(100, Math.round((budgetData?.categorySpent + (detectedProduct?.price || 0)) / budgetData?.categoryBudget * 100)) : 0;
                return (
                  <div className={`rounded-xl p-4 mb-4 border ${canAfford ? 'bg-emerald-500/10 border-emerald-500/20' : 'bg-red-500/10 border-red-500/20'}`}>
                      <div className="flex items-center gap-2 mb-3">
                        <Icon name={canAfford ? 'TrendingUp' : 'AlertTriangle'} size={16} color={canAfford ? '#10B981' : '#EF4444'} />
                        <span className={`font-heading font-semibold text-sm ${canAfford ? 'text-emerald-400' : 'text-red-400'}`}>
                          {canAfford ? 'Within Budget' : 'Exceeds Budget'}
                        </span>
                        <span className="ml-auto text-xs text-slate-500">{budgetData?.category}</span>
                      </div>
                      <div className="space-y-2">
                        <div>
                          <div className="flex justify-between text-xs mb-1">
                            <span className="text-slate-500">Current</span>
                            <span className="font-data text-slate-400">{budgetPercent}%</span>
                          </div>
                          <div className="h-2 bg-slate-700 rounded-full overflow-hidden">
                            <div className="h-full bg-cyan-500 rounded-full" style={{ width: `${budgetPercent}%` }} />
                          </div>
                        </div>
                        <div>
                          <div className="flex justify-between text-xs mb-1">
                            <span className="text-slate-500">After purchase</span>
                            <span className={`font-data ${canAfford ? 'text-emerald-400' : 'text-red-400'}`}>{afterPercent}%</span>
                          </div>
                          <div className="h-2 bg-slate-700 rounded-full overflow-hidden">
                            <div className={`h-full rounded-full ${canAfford ? 'bg-emerald-500' : 'bg-red-500'}`} style={{ width: `${afterPercent}%` }} />
                          </div>
                        </div>
                        <div className="flex justify-between pt-1 border-t border-slate-700">
                          <span className="text-xs text-slate-400">Remaining after</span>
                          <span className={`font-data text-sm font-bold ${canAfford ? 'text-emerald-400' : 'text-red-400'}`}>
                            {canAfford ? `$${afterPurchase?.toFixed(2)}` : `-$${Math.abs(afterPurchase)?.toFixed(2)}`}
                          </span>
                        </div>
                      </div>
                    </div>);

              })()}

                {/* Price comparison */}
                <div className="mb-4">
                  <h4 className="font-heading font-semibold text-sm text-slate-300 mb-2 flex items-center gap-2">
                    <Icon name="BarChart2" size={14} color="#06B6D4" />
                    Price Comparison
                  </h4>
                  <div className="space-y-1.5">
                    {detectedProduct?.priceComparison?.map((item, i) =>
                  <div key={i} className="flex items-center justify-between bg-slate-800/60 rounded-lg px-3 py-2">
                        <span className="text-sm text-slate-300">{item?.store}</span>
                        <div className="flex items-center gap-2">
                          <span className={`font-data text-sm font-semibold ${item?.price === detectedProduct?.price ? 'text-cyan-400' : 'text-slate-300'}`}>
                            ${item?.price?.toFixed(2)}
                          </span>
                          {item?.price < detectedProduct?.price &&
                      <span className="text-xs bg-emerald-400/10 text-emerald-400 px-1.5 py-0.5 rounded">
                              Save ${(detectedProduct?.price - item?.price)?.toFixed(2)}
                            </span>
                      }
                        </div>
                      </div>
                  )}
                  </div>
                </div>

                {/* Alternatives */}
                <AlternativesPanel
                alternatives={MOCK_ALTERNATIVES}
                currentPrice={detectedProduct?.price || 0}
                onSelect={handleAlternativeSelect} />
              

                {/* Actions */}
                <div className="flex gap-3 pt-2">
                  <button
                  onClick={handleSaveWishlist}
                  className={`flex-1 flex items-center justify-center gap-2 py-3 rounded-xl border text-sm font-medium transition-colors min-h-[44px] ${
                  savedToWishlist ?
                  'bg-pink-500/10 border-pink-500/40 text-pink-400' : 'bg-slate-800 border-slate-700 text-slate-300 hover:border-cyan-500/40'}`
                  }>
                  
                    <Icon name="Heart" size={16} color={savedToWishlist ? '#F472B6' : 'currentColor'} />
                    {savedToWishlist ? 'Saved' : 'Wishlist'}
                  </button>
                  <button
                  onClick={handleProceed}
                  className="flex-1 flex items-center justify-center gap-2 py-3 rounded-xl bg-cyan-500 text-slate-900 text-sm font-semibold hover:bg-cyan-400 transition-colors min-h-[44px]">
                  
                    <Icon name="ShoppingCart" size={16} color="#0F172A" />
                    Add to Cart
                  </button>
                </div>

                <button
                onClick={handleReset}
                className="w-full mt-3 py-2.5 rounded-xl border border-slate-700 text-slate-400 text-sm hover:border-slate-600 transition-colors flex items-center justify-center gap-2 min-h-[44px]">
                
                  <Icon name="RotateCcw" size={14} color="currentColor" />
                  Scan Another Product
                </button>
              </div>
            }
          </div>

          {/* Mobile: slide-up product panel */}
          <div className="lg:hidden">
            {isDetected && detectedProduct &&
            <ProductInfoPanel
              product={detectedProduct}
              budgetData={budgetData}
              onSaveWishlist={handleSaveWishlist}
              onProceed={handleProceed}
              onDismiss={handleReset}
              savedToWishlist={savedToWishlist} />

            }
            {isDetected && detectedProduct &&
            <div className="absolute bottom-[420px] left-0 right-0 z-20 px-4">
                <AlternativesPanel
                alternatives={MOCK_ALTERNATIVES}
                currentPrice={detectedProduct?.price}
                onSelect={handleAlternativeSelect} />
              
              </div>
            }
          </div>
        </div>
      </div>
      {/* Manual Search Panel */}
      <ManualSearchPanel
        isOpen={showManualSearch}
        onClose={() => setShowManualSearch(false)}
        onProductSelect={(p) => {
          setDetectedProduct(p);
          setIsDetected(true);
          setSavedToWishlist(false);
        }}
        mockProducts={MOCK_PRODUCTS} />
      {/* Purchase Decision Modal */}
      <PurchaseDecisionModal
        isOpen={showPurchaseModal}
        product={detectedProduct}
        onClose={() => setShowPurchaseModal(false)}
        onConfirm={handleConfirmPurchase} />
      {/* Purchase success toast */}
      <PurchaseSuccessToast
        toast={purchaseToast}
        onDismiss={() => setPurchaseToast(null)}
      />
      {/* Toast notification */}
      <AnimatePresence>
        {toastMsg &&
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: 40 }}
          className="fixed bottom-24 md:bottom-6 left-1/2 -translate-x-1/2 z-50 bg-slate-800 border border-cyan-500/30 text-slate-200 text-sm px-5 py-3 rounded-xl shadow-glow-md flex items-center gap-2 whitespace-nowrap">
          
            <Icon name="CheckCircle" size={16} color="#10B981" />
            {toastMsg}
          </motion.div>
        }
      </AnimatePresence>
    </div>
  );


}